// Path: ./src/app/pages/UserPage/useAdminUserTableColumns.tsx
// setTableColumn.tsx

import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { useTranslation } from "react-i18next";
import {
  GfAdminUser,
  GfAdminUserDto,
  GfRole,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";
import { RenderDeleteButton } from "../../components/RenderDeleteButtonTable";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";

export const useAdminUserTableColumns = (
  handleOpenDelete: (value: GfAdminUserDto) => void,
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const values: AllowedTableColumn[] = ["updatedAt", "createdAt"];
  /**
   * Handles the click event to navigate to the merchant editing page with pre-filled data.
   *
   * @param value - The EditProps containing merchant details for editing.
   */
  const handleOnClickEdit = async (value: GfAdminUser) => {
    navigate(routes.user.editById(value.id));
  };

  const columns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["user"]}
          >
            <RenderEditButton
              onClick={handleOnClickEdit}
              {...props}
            ></RenderEditButton>
          </PermissionComponentWrapper>
        );
      },
      sortable: false,
    },
    {
      width: 200,
      field: "userName",
      headerName: `${t(messages["userName"]())}`,
    },
    {
      width: 200,
      field: "email",
      headerName: `${t(messages["email"]())}`,
    },
    {
      width: 200,
      field: "roles",
      headerName: `${t(messages["roles"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfAdminUser>) => {
        const roles: GfRole[] = params.row.roles;
        return <div>{roles.map((tag) => tag.name).join(", ")}</div>;
      },
    },

    {
      field: "merchant",
      width: 200,
      headerName: `${t(messages["Merchant"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfAdminUser>) => {
        const merchant = params.row.merchant?.[0];
        return <div>{merchant && merchant.name}</div>;
      },
    },

    {
      field: "gfFlyerItemCategory",
      width: 200,
      headerName: `${t(messages["gfFlyerItemCategory"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfAdminUser>) => {
        const flyerTag = params.row.gfFlyerItemCategory;
        return <div>{flyerTag?.map((tag) => tag.name).join(", ")}</div>;
      },
    },
    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),
    {
      field: "firstName",
      width: 200,
      headerName: `${t(messages["firstName"]())}`,
    },
    {
      field: "lastName",
      width: 200,
      headerName: `${t(messages["lastName"]())}`,
    },
    {
      field: "id",
      width: 300,
      headerName: `${t(messages["id"]())}`,
      filterable: false,
    },
    {
      field: "delete",
      headerName: " ",
      width: 90,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <RenderDeleteButton
            onClick={handleOpenDelete}
            {...props}
          ></RenderDeleteButton>
        );
      },
      sortable: false,
    },
  ];

  return columns;
};
